import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useChild } from "../lib/childContext";
import { toast } from "sonner";
import ChildSwitcher from "../components/ChildSwitcher";
import Brand from "../components/Brand";
import { Lock, Eye, Send, Activity, Copy, UserPlus, History, Download, Trash2, Shield, X } from "lucide-react";

const TRUST_IMG =
  "https://static.prod-images.emergentagent.com/jobs/5b7539fb-c408-4336-acd4-f414390d8b8e/images/9146ea81daa3fede3f87c81ea21a29df8ca9a82bd24744a4cc5f94903fbc13e2.png";

const PROMISES = [
  { key: "encrypted", title: "End-to-end encrypted", body: "Only the people you've named can ever read what's shared here.", icon: Lock },
  { key: "minimal", title: "Data minimised", body: "Alouvya collects only what's needed to hold a calm thread. Nothing more.", icon: Eye },
  { key: "no-ads", title: "Zero ads, zero tracking", body: "There is no second customer. You are the only one we serve.", icon: Send },
  { key: "auditable", title: "Auditable, always", body: "Every connection is named. Every permission is yours to revoke.", icon: Activity },
];

const PERM_LABEL = {
  can_view_moments: "view moments shared about the child",
  can_send_messages: "send and receive private letters",
  can_update_state: "update the ambient presence state",
};

function timeAgoOrDate(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} d ago`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function Trust() {
  const { user, forgetMe } = useAuth();
  const navigate = useNavigate();
  const { selected } = useChild();
  const [perms, setPerms] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [invite, setInvite] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inventory, setInventory] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  useEffect(() => {
    if (!selected) {
      setPerms(null);
      setHistory([]);
      return;
    }
    api.get(`/children/${selected.id}/permissions`).then((r) => setPerms(r.data));
    api
      .get(`/children/${selected.id}/permissions/history`)
      .then((r) => setHistory(r.data));
  }, [selected]);

  // Load inventory + email-status once
  useEffect(() => {
    api.get("/users/me/inventory").then((r) => setInventory(r.data)).catch(() => {});
    api
      .get("/users/me/email-status")
      .then((r) => setEmailConfigured(r.data.email_configured))
      .catch(() => {});
  }, []);

  const exportMyData = async () => {
    setExportBusy(true);
    try {
      const r = await api.get("/users/me/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `orbit-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast("Downloaded. Everything we hold, in your hands.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't prepare the export.");
    } finally {
      setExportBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (deleteConfirm.trim().toLowerCase() !== "delete") {
      toast("Please type DELETE to confirm.");
      return;
    }
    setDeleteBusy(true);
    try {
      const r = await forgetMe();
      toast(r.detail || "Your account is gone, gently.");
      navigate("/");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't complete the deletion.");
    } finally {
      setDeleteBusy(false);
    }
  };

  const toggle = async (key) => {
    if (!perms || !selected) return;
    if (user?.role !== "parent") {
      toast("Only a parent can change these.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/children/${selected.id}/permissions`, {
        [key]: !perms[key],
      });
      setPerms(r.data);
      const h = await api.get(`/children/${selected.id}/permissions/history`);
      setHistory(h.data);
      toast("Saved. Quietly.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't change just now.");
    } finally {
      setSaving(false);
    }
  };

  const createInvite = async () => {
    if (!selected) return;
    setInviteBusy(true);
    try {
      const r = await api.post(`/children/${selected.id}/coparent/invite`);
      setInvite(r.data);
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't create the invite right now.");
    } finally {
      setInviteBusy(false);
    }
  };

  const copyLink = () => {
    if (!invite) return;
    const url = `${window.location.origin}/accept/coparent/${invite.token}`;
    navigator.clipboard?.writeText(url);
    toast("Link copied. Share it gently.");
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#FBF9F6]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-4">
          <ChildSwitcher />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 sm:px-10 pt-6 pb-12 fade-up">
        <span className="eyebrow">Trust, visible</span>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
          Your data, <em className="italic text-[#8A9984]">your room.</em>
        </h1>
        <p className="mt-6 text-lg text-[#6A6863] font-light max-w-2xl">
          Privacy is not a settings page hidden behind a menu. It lives in plain
          sight, in language you can read, with controls you can feel.
        </p>

        {/* Promises bento */}
        <section className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden">
          <div
            className="md:row-span-2 relative min-h-[240px] md:min-h-0"
            style={{
              backgroundImage: `url(${TRUST_IMG})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          {PROMISES.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.key}
                data-testid={`trust-promise-${p.key}`}
                className="bg-[#FBF9F6] p-8 sm:p-10 transition-colors duration-500 hover:bg-white"
              >
                <Icon size={16} strokeWidth={1.5} className="text-[#8A9984]" />
                <h3 className="mt-5 font-serif text-2xl tracking-tight">{p.title}</h3>
                <p className="mt-3 text-sm text-[#6A6863] font-light leading-relaxed">
                  {p.body}
                </p>
              </div>
            );
          })}
        </section>

        {/* Permissions */}
        {perms && (
          <section className="mt-20">
            <span className="eyebrow">Permissions for {selected?.name}</span>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight">
              Who can do what.
            </h2>
            <p className="mt-4 text-base text-[#6A6863] font-light max-w-xl">
              {perms.caregiver_name
                ? `${perms.caregiver_name} is your caregiver.`
                : "No caregiver is connected yet."}{" "}
              {user?.role === "parent"
                ? "Toggle anything. You are the data owner."
                : "Only a parent can change these."}
            </p>

            <ul className="mt-10 space-y-px bg-[#2C2B29]/8 rounded-3xl overflow-hidden border hairline">
              <PermRow
                label="View moments shared about your child"
                value={perms.can_view_moments}
                onToggle={() => toggle("can_view_moments")}
                disabled={saving || user?.role !== "parent"}
                testId="perm-view-moments"
              />
              <PermRow
                label="Send and receive private letters"
                value={perms.can_send_messages}
                onToggle={() => toggle("can_send_messages")}
                disabled={saving || user?.role !== "parent"}
                testId="perm-send-messages"
              />
              <PermRow
                label="Update the ambient presence state"
                value={perms.can_update_state}
                onToggle={() => toggle("can_update_state")}
                disabled={saving || user?.role !== "parent"}
                testId="perm-update-state"
              />
            </ul>
          </section>
        )}

        {/* Co-parent invite */}
        {user?.role === "parent" && perms && (
          <section className="mt-20">
            <span className="eyebrow">Co-parent</span>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight">
              {perms.parent_ids.length > 1
                ? `${perms.parent_names.length} parents hold ${selected?.name}.`
                : "Invite another parent."}
            </h2>
            <p className="mt-4 text-base text-[#6A6863] font-light max-w-xl">
              Share one quiet link. It works once, and gently expires in 7 days.
            </p>

            <div className="mt-8 rounded-3xl border hairline bg-white p-7 sm:p-9">
              {perms.parent_names.length > 1 && (
                <div className="mb-6 flex flex-wrap gap-2">
                  {perms.parent_names.map((n) => (
                    <span key={n} className="trust-pill">{n}</span>
                  ))}
                </div>
              )}
              {invite ? (
                <div data-testid="coparent-invite-result">
                  <div className="eyebrow">Invite link · works once</div>
                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <code className="text-sm font-mono bg-[#FBF9F6] border hairline rounded-full px-4 py-2 text-[#2C2B29] break-all">
                      {window.location.origin}/accept/coparent/{invite.token}
                    </code>
                    <button
                      type="button"
                      onClick={copyLink}
                      data-testid="coparent-invite-copy"
                      className="btn-primary !py-2 !px-4 !text-xs"
                    >
                      Copy
                      <Copy size={12} strokeWidth={1.5} />
                    </button>
                  </div>
                  <p className="mt-4 text-xs text-[#6A6863]">
                    Expires {new Date(invite.expires_at).toLocaleDateString()}.
                  </p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={createInvite}
                  disabled={inviteBusy}
                  data-testid="coparent-invite-create"
                  className="btn-primary"
                >
                  {inviteBusy ? "A moment…" : "Generate invite link"}
                  <UserPlus size={14} strokeWidth={1.5} />
                </button>
              )}
            </div>
          </section>
        )}

        {/* Trusted caregivers */}
        {user?.role === "parent" && perms && (
          <CaregiversSection
            child={selected}
            perms={perms}
            onChanged={async () => {
              if (!selected) return;
              const r = await api.get(`/children/${selected.id}/permissions`);
              setPerms(r.data);
            }}
          />
        )}

        {/* Trusted pickup adults */}
        {user?.role === "parent" && selected && (
          <PickupAuthSection child={selected} />
        )}

        {/* Audit history */}
        <section className="mt-20">
          <div className="flex items-center gap-3">
            <History size={14} strokeWidth={1.5} className="text-[#8A9984]" />
            <span className="eyebrow">Audit timeline</span>
          </div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight">
            Every change, named.
          </h2>
          <p className="mt-4 text-base text-[#6A6863] font-light max-w-xl">
            A quiet record of every permission ever changed. Nothing happens here without leaving a trace.
          </p>

          <ol
            data-testid="audit-list"
            className="mt-10 space-y-8 border-l hairline pl-6"
          >
            {history.length === 0 && (
              <li
                data-testid="audit-empty"
                className="font-serif italic text-[#6A6863]"
              >
                Nothing has been changed yet. A still page.
              </li>
            )}
            {history.map((h, i) => (
              <li
                key={h.id}
                data-testid={`audit-entry-${i}`}
                className="relative"
              >
                <span className="absolute -left-[29px] top-2 h-2 w-2 rounded-full bg-[#9BA995]" />
                <div className="eyebrow">{timeAgoOrDate(h.at)}</div>
                <p className="mt-2 font-serif text-lg text-[#2C2B29]">
                  <em className="italic text-[#8A9984]">{h.changed_by_name}</em>{" "}
                  changed{" "}
                  {Object.entries(h.changes)
                    .map(
                      ([k, v]) =>
                        `${PERM_LABEL[k] || k} to ${v ? "allowed" : "paused"}`
                    )
                    .join(", ")}
                  .
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* Data we hold */}
        <section className="mt-20" data-testid="data-rights-section">
          <div className="flex items-center gap-3">
            <Shield size={14} strokeWidth={1.5} className="text-[#8A9984]" />
            <span className="eyebrow">Data we hold about you</span>
          </div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight">
            Your room, your keys.
          </h2>
          <p className="mt-4 text-base text-[#6A6863] font-light max-w-xl">
            Below is the full ledger of what Alouvya holds. Download all of it, anytime. Close
            the account, and we let go — gracefully.
          </p>

          {inventory && (
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden">
              {Object.entries(inventory.counts).map(([k, v]) => (
                <div
                  key={k}
                  data-testid={`inventory-${k}`}
                  aria-label={`${k.replace(/_/g, " ")}: ${v}`}
                  className="bg-[#FBF9F6] p-6 sm:p-7"
                >
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#6A6863]">
                    {k.replace(/_/g, " ")}
                  </div>
                  <div
                    data-testid={`inventory-${k}-count`}
                    className="mt-2 font-serif text-3xl text-[#2C2B29]"
                  >
                    {v}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={exportMyData}
              disabled={exportBusy}
              data-testid="data-export-btn"
              className="btn-primary"
            >
              {exportBusy ? "Preparing…" : "Download everything"}
              <Download size={14} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setShowDelete(true)}
              data-testid="data-delete-open"
              className="btn-ghost"
            >
              Close my account
              <Trash2 size={14} strokeWidth={1.5} />
            </button>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            <span className="trust-pill" data-testid="trust-region-pill">
              region · {user?.region || "GLOBAL"}
            </span>
            <span className="trust-pill">
              <Shield size={11} strokeWidth={1.5} />
              public key on file ·{" "}
              {user?.public_key_jwk ? "yes" : "not yet"}
            </span>
            <span className="trust-pill">
              digest email ·{" "}
              {emailConfigured
                ? user?.digest_email_opt_in
                  ? "on"
                  : "available"
                : "not connected"}
            </span>
          </div>
        </section>
      </main>

      {showDelete && (
        <div
          data-testid="data-delete-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#FBF9F6]/85 backdrop-blur-md px-4"
        >
          <div className="w-full max-w-lg bg-white rounded-3xl border hairline p-8 sm:p-10 fade-up">
            <span className="eyebrow">A gentle ending</span>
            <h2 className="mt-3 font-serif text-3xl tracking-tight">
              Close the account?
            </h2>
            <p className="mt-5 text-[#6A6863] font-light">
              If you proceed, any child where you are the sole parent will be removed
              — moments, letters, history, and all. For shared children, only your
              presence is gently lifted. Letters you sent stay with the people who
              received them, with your name respectfully redacted. The safety audit
              log remains, anonymised.
            </p>
            <p className="mt-6 text-sm text-[#6A6863]">
              Type <strong className="font-medium text-[#2C2B29]">DELETE</strong> to confirm.
            </p>
            <input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              data-testid="data-delete-confirm-input"
              className="orbit-input mt-3"
            />
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteBusy || deleteConfirm.trim().toUpperCase() !== "DELETE"}
                data-testid="data-delete-confirm"
                className="btn-primary"
              >
                {deleteBusy ? "Letting go…" : "Yes, close gently"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDelete(false);
                  setDeleteConfirm("");
                }}
                data-testid="data-delete-cancel"
                className="btn-ghost"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PermRow({ label, value, onToggle, disabled, testId }) {
  return (
    <li className="bg-[#FBF9F6] flex items-center justify-between gap-6 p-7 sm:p-8">
      <span className="text-base text-[#2C2B29] font-light max-w-xl">
        {label}
      </span>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        data-testid={testId}
        aria-pressed={value}
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-500 ${
          value ? "bg-[#9BA995]" : "bg-[#2C2B29]/15"
        } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-500 ${
            value ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </li>
  );
}


function CaregiversSection({ child, perms, onChanged }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const { refresh: refreshChildren } = useChild();

  const add = async (e) => {
    e.preventDefault();
    if (!child || !email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/children/${child.id}/caregivers`, {
        caregiver_email: email.trim().toLowerCase(),
      });
      setEmail("");
      await onChanged();
      await refreshChildren();
      toast("A trusted adult, gently added.");
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't add right now.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cgId) => {
    if (!child) return;
    try {
      await api.delete(`/children/${child.id}/caregivers/${cgId}`);
      await onChanged();
      await refreshChildren();
      toast("Caregiver gently removed.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't remove.");
    }
  };

  const pairs = (perms?.caregiver_ids || []).map((id, i) => ({
    id,
    name: perms.caregiver_names?.[i] || "—",
  }));

  return (
    <section className="mt-20" data-testid="trust-caregivers-section">
      <span className="eyebrow">Trusted caregivers</span>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight">
        {pairs.length === 0
          ? `Add a trusted adult for ${child?.name || "your child"}.`
          : pairs.length === 1
          ? `One trusted adult for ${child?.name}.`
          : `${pairs.length} trusted adults for ${child?.name}.`}
      </h2>
      <p className="mt-4 text-base text-[#6A6863] font-light max-w-xl">
        Real families have more than one caring grown-up — a grandparent, a sitter, a co-teacher.
        Each one you add here can quietly share moments, no more and no less.
      </p>

      <ul
        data-testid="trust-caregivers-list"
        className="mt-10 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
      >
        {pairs.length === 0 && (
          <li
            data-testid="trust-caregivers-empty"
            className="bg-[#FBF9F6] p-7 font-serif italic text-[#6A6863]"
          >
            No one yet. Add someone you trust by email below.
          </li>
        )}
        {pairs.map((cg) => (
          <li
            key={cg.id}
            data-testid={`trust-caregiver-row-${cg.id}`}
            className="bg-[#FBF9F6] p-6 flex items-center justify-between gap-4 flex-wrap"
          >
            <div>
              <div className="font-serif text-lg">{cg.name}</div>
              <div className="text-xs text-[#6A6863] mt-1">
                shares moments · updates ambient state · operates the kiosk
              </div>
            </div>
            <button
              type="button"
              onClick={() => remove(cg.id)}
              data-testid={`trust-caregiver-remove-${cg.id}`}
              aria-label="Remove caregiver"
              className="text-[#6A6863] hover:text-[#2C2B29] transition-colors p-2"
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </li>
        ))}
      </ul>

      <form
        onSubmit={add}
        data-testid="trust-add-caregiver-form"
        className="mt-8 rounded-3xl border hairline bg-white p-6 sm:p-8 flex flex-wrap gap-4 items-end"
      >
        <div className="flex-1 min-w-[220px]">
          <label className="eyebrow">Add by email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="grandma@trusted.place"
            data-testid="trust-add-caregiver-email"
            className="orbit-input mt-3"
          />
          {error && (
            <p
              data-testid="trust-add-caregiver-error"
              className="mt-3 text-xs text-[#2C2B29] italic font-serif"
            >
              {error}
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={busy || !email.trim()}
          data-testid="trust-add-caregiver-submit"
          className="btn-primary"
        >
          {busy ? "Adding…" : "Add gently"}
          <UserPlus size={14} strokeWidth={1.5} />
        </button>
      </form>
      <p className="mt-3 text-xs text-[#6A6863]">
        They need to already have a caregiver account on Alouvya. Their email is all we ask.
      </p>
    </section>
  );
}


const PICKUP_RELATIONS = [
  { value: "parent", label: "Parent" },
  { value: "grandparent", label: "Grandparent" },
  { value: "sibling", label: "Sibling" },
  { value: "aunt_uncle", label: "Aunt / Uncle" },
  { value: "family_friend", label: "Family friend" },
  { value: "sitter", label: "Sitter" },
  { value: "neighbor", label: "Neighbor" },
  { value: "other", label: "Other" },
];

function PickupAuthSection({ child }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "",
    relation: "grandparent",
    phone: "",
    note: "",
    valid_until: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!child?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/children/${child.id}/pickup-authorizations`);
      setList(r.data || []);
    } catch {
      // quiet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child?.id]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await api.post(`/children/${child.id}/pickup-authorizations`, {
        name: form.name.trim(),
        relation: form.relation,
        phone: form.phone.trim() || null,
        note: form.note.trim() || null,
        valid_until: form.valid_until || null,
      });
      setForm({
        name: "",
        relation: "grandparent",
        phone: "",
        note: "",
        valid_until: "",
      });
      await load();
      toast("Trusted, gently.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/children/${child.id}/pickup-authorizations/${id}`);
      await load();
      toast("Removed.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't remove.");
    }
  };

  return (
    <section className="mt-20" data-testid="trust-pickup-section">
      <span className="eyebrow">Trusted pickups</span>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight">
        Who may collect {child?.name || "your child"}?
      </h2>
      <p className="mt-4 text-base text-[#6A6863] font-light max-w-xl">
        Add the trusted adults who can pick up your child. Leave the &ldquo;until&rdquo; date
        blank for an always-authorized adult; set a date for a one-day arrangement
        (&ldquo;Today, Grandma is collecting&rdquo;).
      </p>

      <ul
        data-testid="trust-pickup-list"
        className="mt-10 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
      >
        {loading && (
          <li className="bg-[#FBF9F6] p-6 font-serif italic text-[#6A6863]">
            A quiet moment…
          </li>
        )}
        {!loading && list.length === 0 && (
          <li
            data-testid="trust-pickup-empty"
            className="bg-[#FBF9F6] p-7 font-serif italic text-[#6A6863]"
          >
            No trusted pickups yet. Add one below.
          </li>
        )}
        {list.map((p) => (
          <li
            key={p.id}
            data-testid={`trust-pickup-row-${p.id}`}
            className="bg-[#FBF9F6] p-6 flex items-start justify-between gap-4 flex-wrap"
          >
            <div className="flex-1 min-w-[200px]">
              <div className="font-serif text-lg text-[#2C2B29]">{p.name}</div>
              <div className="text-xs text-[#6A6863] mt-1 capitalize">
                {(p.relation || "").replace("_", " ")}
                {p.phone ? ` · ${p.phone}` : ""}
                {p.valid_until ? ` · only ${p.valid_until}` : " · always"}
              </div>
              {p.note && (
                <div className="text-sm text-[#2C2B29] mt-2 italic font-serif">
                  {p.note}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => remove(p.id)}
              data-testid={`trust-pickup-remove-${p.id}`}
              aria-label="Remove"
              className="text-[#6A6863] hover:text-[#2C2B29] transition-colors p-2"
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </li>
        ))}
      </ul>

      <form
        onSubmit={add}
        data-testid="trust-pickup-form"
        className="mt-8 rounded-3xl border hairline bg-white p-6 sm:p-8 grid sm:grid-cols-2 gap-5"
      >
        <div>
          <label className="eyebrow">Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Grandma Rose"
            data-testid="trust-pickup-name"
            className="orbit-input mt-3"
            required
          />
        </div>
        <div>
          <label className="eyebrow">Relationship</label>
          <select
            value={form.relation}
            onChange={(e) => setForm({ ...form, relation: e.target.value })}
            data-testid="trust-pickup-relation"
            className="orbit-input mt-3 bg-transparent"
          >
            {PICKUP_RELATIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="eyebrow">Phone (optional)</label>
          <input
            type="text"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="for staff to reach them"
            data-testid="trust-pickup-phone"
            className="orbit-input mt-3"
          />
        </div>
        <div>
          <label className="eyebrow">Only until (optional)</label>
          <input
            type="date"
            value={form.valid_until}
            onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
            data-testid="trust-pickup-valid-until"
            className="orbit-input mt-3"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="eyebrow">Note (optional)</label>
          <input
            type="text"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="anything staff should know — comfort items, languages…"
            data-testid="trust-pickup-note"
            className="orbit-input mt-3"
          />
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <button
            type="submit"
            disabled={busy || !form.name.trim()}
            data-testid="trust-pickup-submit"
            className="btn-primary"
          >
            {busy ? "Adding…" : "Add trusted pickup"}
            <Shield size={14} strokeWidth={1.5} />
          </button>
        </div>
      </form>
    </section>
  );
}
